import React from 'react';

import {
  Grid,
  Paper,
  Typography,
  MenuItem,
  Tooltip,
  Chip,
  TextField,
  Button,
  Box,
  Select,
  OutlinedInput,
} from '@mui/material';

import { useState, useEffect } from 'react';
import { supabase } from '../../../api';
import Avatarr from '../Avatar/Avatar';
import { PROFILE_TABLE } from '../../../constants/supabase_constants';

// ******************************************************************************************************************************

const EditProfile = ({ session, membershipLevel }) => {
  const [loading, setLoading] = useState(true);

  const [firstName, setFirstName] = useState(null);
  const [lastName, setLastName] = useState(null);
  // eslint-disable-next-line
  const [fullName, setFullName] = useState(null);
  const [about, setAbout] = useState(null);
  const [title, setTitle] = useState(null);
  const [age, setAge] = useState(null);
  const [skills, setSkills] = useState([]);
  const [availableTimings, setAvailableTimings] = useState([]);

  const [companyName, setCompanyName] = useState(null);
  const [companyAbout, setCompanyAbout] = useState(null);
  const [companyLink, setCompanyLink] = useState(null);
  const [founded, setFounded] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [totalFunding, setTotalFunding] = useState(null);
  const [teamSize, setTeamSize] = useState(null);
  const [industry, setIndustry] = useState([]);

  const [linkedin, setLinkedIn] = useState(null);
  const [email, setEmail] = useState(null);
  const [mobile, setMobile] = useState(null);
  const [avatar_url, setAvatarUrl] = useState(null);

  const [addressLine1, setAddressLine1] = useState(null);
  const [addressLine2, setAddressLine2] = useState(null);
  const [postalCode, setPostalCode] = useState(null);

  const [bookingMethod, setBookingMethod] = useState(null);

  const [onboardingCompleted, setOnboardingCompleted] = useState(null);

  useEffect(() => {
    getProfile();
    // eslint-disable-next-line
  }, [onboardingCompleted]);

  const skillsList = [
    'Bootstrapping',
    'Building a team',
    'Content marketing',
    'Conversion rate optimization',
    'Copywriting',
    'Customer success',
    'Data science',
    'Design / UX',
    'Email marketing',
    'Facebook ads',
    'Go to market strategy',
    'Google ads',
    'Growth marketing',
    'Idea validation',
    'Link building',
    'Operations',
    'Marketing automation',
    'No-code',
    'Performance marketing',
    'PPC strategies',
    'Pricing strategy',
    'Product analytics',
    'Product management',
    'Product market fit',
    'Public relations',
    'Raising funding',
    'Sales',
    'SEO',
    'Social media',
    'User research',
    'Web Development',
  ];

  const revenueList = [
    'Private',
    '< $100 ARR',
    '< $1,000 ARR',
    '< $10,000 ARR',
    '< $100,000 ARR',
    '< $1,000,000 ARR',
    '> $1,000,000 ARR',
  ];

  const handleSkillsChange = (event) => {
    const {
      target: { value },
    } = event;
    setSkills(
      // On autofill we get a stringified value.
      typeof value === 'string' ? value.split(',') : value,
    );
  };

  const getProfile = async () => {
    try {
      setLoading(true);
      const user = supabase.auth.user();

      let { data, error, status } = await supabase
        .from(PROFILE_TABLE)
        .select(
          `firstName, lastName, fullName, title, about, age, skills, availableTimings, companyName, companyAbout, companyLink, founded, revenue, totalFunding, teamSize, industry, linkedin, email, mobile, avatar_url, addressLine1, addressLine2, postalCode, onboardingCompleted, bookingMethod`,
        )
        .eq('id', user.id)
        .single();

      if (error && status !== 406) {
        throw error;
      }

      setEmail(user.email);

      if (data) {
        setLinkedIn(data.linkedin);
        setMobile(data.mobile);
        setFirstName(data.firstName);
        setLastName(data.lastName);
        setFullName(data.fullName);
        setAbout(data.about);
        setTitle(data.title);
        setAge(data.age);
        setSkills(data.skills);
        setAvailableTimings(data.availableTimings);
        setCompanyName(data.companyName);
        setCompanyAbout(data.companyAbout);
        setCompanyLink(data.companyLink);
        setFounded(data.founded);
        setRevenue(data.revenue);
        setTotalFunding(data.totalFunding);
        setTeamSize(data.teamSize);
        setIndustry(data.industry);
        setAvatarUrl(data.avatar_url);
        setAddressLine1(data.addressLine1);
        setAddressLine2(data.addressLine2);
        setPostalCode(data.postalCode);
        setBookingMethod(data.bookingMethod);
        setOnboardingCompleted(data.onboardingCompleted);
      }
    } catch (error) {
      alert(error.message);
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      const user = supabase.auth.user();

      const updates = {
        id: user.id,
        firstName,
        lastName,
        fullName: firstName + ' ' + lastName,
        linkedin,
        email,
        mobile,
        about,
        title,
        age,
        skills,
        availableTimings,
        companyName,
        companyAbout,
        companyLink,
        founded,
        revenue,
        totalFunding,
        teamSize,
        avatar_url,
        industry,
        addressLine1,
        addressLine2,
        postalCode,
        updated_at: new Date(),
      };

      let { error } = await supabase.from(PROFILE_TABLE).upsert(updates, {
        returning: 'minimal', // Don't return the value after inserting
      });

      if (error) {
        throw error;
      }
    } catch (error) {
      alert(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div aria-live='polite'>
      {loading ? (
        'Saving ...'
      ) : (
        <Grid>
          <Box component='form' onSubmit={updateProfile} noValidate sx={{ mt: 1 }}>
            <Paper
              elevation={20}
              sx={{
                padding: '2rem',
                mt: '2rem',
                textAlign: 'center',
                position: 'relative',
              }}>
              {!onboardingCompleted && membershipLevel !== 'mentor' && (
                <Box
                  id='overlay'
                  sx={{
                    position: 'absolute',
                    margin: '-2rem',
                    width: '100%',
                    height: '100%',
                    backdropFilter: 'blur(8px)',
                    zIndex: '1000',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}>
                  <Button variant='contained'>Complete onboarding first</Button>
                </Box>
              )}
              <Box
                display='flex'
                justifyContent='center'
                alignItems='center'
                className='form-widget'
                sx={{
                  width: 250,
                  height: 150,
                  mx: 'auto',
                  mb: '15px',
                  mt: '15px',
                }}>
                <Avatarr
                  url={avatar_url}
                  size={150}
                  onUpload={(url) => {
                    setAvatarUrl(url);
                    updateProfile({ avatar_url: url });
                  }}
                />
              </Box>

              {membershipLevel === 'member' && (
                <Tooltip title='You are a member of The 100 Club.'>
                  <Chip label='Member' color='primary' sx={{ mt: '1.5rem' }} />
                </Tooltip>
              )}
              {membershipLevel === 'mentor' && (
                <Tooltip title='You are a mentor in The 100 Club.'>
                  <Chip label='Mentor' color='primary' sx={{ mt: '1.5rem' }} />
                </Tooltip>
              )}
              {membershipLevel === 'member+mentor' && (
                <Tooltip title='You are a member + mentor in The 100 Club.'>
                  <Chip label='Member + Mentor' color='primary' sx={{ mt: '1.5rem' }} />
                </Tooltip>
              )}
            </Paper>
            {membershipLevel !== 'member' && (
              <Paper
                elevation={20}
                sx={{
                  padding: '2rem',
                  mt: '2rem',
                  textAlign: 'left',
                  position: 'relative',
                }}>
                {!onboardingCompleted && membershipLevel !== 'mentor' && (
                  <Box
                    id='overlay'
                    sx={{
                      position: 'absolute',
                      margin: '-2rem',
                      width: '100%',
                      height: '100%',
                      backdropFilter: 'blur(8px)',
                      zIndex: '1000',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}>
                    <Button variant='contained'>Complete onboarding first</Button>
                  </Box>
                )}

                <Typography variant='h5' component='h2' sx={{ fontWeight: 'bold', marginBottom: '1.5rem' }}>
                  You're a Mentor!
                </Typography>
                {membershipLevel === 'mentor' && (
                  <div>
                    <Typography variant='subtitle2' component='h3' sx={{ fontWeight: 'bold' }}>
                      First Name
                    </Typography>
                    <TextField
                      hiddenLabel
                      InputProps={{ 'aria-label': 'firstName' }}
                      fullWidth
                      size='small'
                      variant='outlined'
                      id='firstName'
                      type='url'
                      value={firstName || ''}
                      onChange={(e) => setFirstName(e.target.value)}
                      sx={{ mb: '1.5rem' }}
                    />
                    <Typography variant='subtitle2' component='h3' sx={{ fontWeight: 'bold' }}>
                      Last Name
                    </Typography>
                    <TextField
                      hiddenLabel
                      InputProps={{ 'aria-label': 'lastName' }}
                      fullWidth
                      size='small'
                      variant='outlined'
                      id='lastName'
                      type='url'
                      value={lastName || ''}
                      onChange={(e) => setLastName(e.target.value)}
                      sx={{ mb: '1.5rem' }}
                    />
                    {/* TITLE */}
                    <Typography variant='subtitle2' component='h3' sx={{ fontWeight: 'bold' }}>
                      Title
                    </Typography>
                    <TextField
                      hiddenLabel
                      fullWidth
                      size='small'
                      variant='outlined'
                      id='title'
                      type='url'
                      value={title || ''}
                      onChange={(e) => setTitle(e.target.value)}
                      sx={{ mb: '1.5rem' }}
                    />
                    {/* ABOUT YOU */}
                    <Typography variant='subtitle2' component='h3' sx={{ fontWeight: 'bold' }}>
                      Describe yourself in 2 sentences.
                    </Typography>
                    <TextField
                      inputProps={{ maxLength: 150 }}
                      hiddenLabel
                      multiline
                      rows={3}
                      fullWidth
                      size='small'
                      variant='outlined'
                      id='about'
                      type='url'
                      value={about || ''}
                      onChange={(e) => setAbout(e.target.value)}
                      sx={{ mb: '1.5rem' }}
                    />
                    <Typography variant='subtitle2' component='h3' sx={{ fontWeight: 'bold' }}>
                      LinkedIn
                    </Typography>
                    <TextField
                      hiddenLabel
                      fullWidth
                      size='small'
                      variant='outlined'
                      id='linkedin'
                      type='url'
                      value={linkedin || ''}
                      onChange={(e) => setLinkedIn(e.target.value)}
                      sx={{ mb: '1.5rem' }}
                    />
                  </div>
                )}
                <Typography variant='subtitle2' component='h3' sx={{ fontWeight: 'bold' }}>
                  How should a member book some time with you? (Calendly, Email, etc.)
                </Typography>
                <TextField
                  inputProps={{ maxLength: 150 }}
                  hiddenLabel
                  multiline
                  rows={3}
                  fullWidth
                  size='small'
                  variant='outlined'
                  id='about'
                  type='url'
                  value={bookingMethod || ''}
                  onChange={(e) => setBookingMethod(e.target.value)}
                  sx={{ mb: '1.5rem' }}
                />
                {/* SKILLS & INTERESTS */}
                {(membershipLevel === 'member+mentor' || membershipLevel === 'mentor') && (
                  <div>
                    <Typography variant='subtitle2' component='h3' sx={{ fontWeight: 'bold' }}>
                      What can you offer advice about? (5 max)
                    </Typography>
                    <Select
                      fullWidth
                      size='small'
                      multiple
                      value={skills || ''}
                      onChange={handleSkillsChange}
                      sx={{ mb: '1.5rem' }}
                      input={<OutlinedInput id='select-multiple-chip' label='Chip' />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip key={value} label={value} />
                          ))}
                        </Box>
                      )}>
                      {skillsList.map((name) => (
                        <MenuItem key={name} value={name}>
                          {name}
                        </MenuItem>
                      ))}
                    </Select>
                  </div>
                )}
              </Paper>
            )}

            {(membershipLevel === 'member' || membershipLevel === 'member+mentor') && (
              <Paper
                elevation={20}
                sx={{
                  padding: '2rem',
                  mt: '2rem',
                  textAlign: 'left',
                  position: 'relative',
                }}>
                {!onboardingCompleted && membershipLevel !== 'mentor' && (
                  <Box
                    id='overlay'
                    sx={{
                      position: 'absolute',
                      margin: '-2rem',
                      width: '100%',
                      height: '100%',
                      backdropFilter: 'blur(8px)',
                      zIndex: '1000',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}>
                    <Button variant='contained'>Complete onboarding first</Button>
                  </Box>
                )}
                {/* FULL NAME */}
                <Typography variant='subtitle2' component='h3' sx={{ fontWeight: 'bold' }}>
                  First Name
                </Typography>
                <TextField
                  hiddenLabel
                  InputProps={{ 'aria-label': 'firstName' }}
                  fullWidth
                  size='small'
                  variant='outlined'
                  id='firstName'
                  type='url'
                  value={firstName || ''}
                  onChange={(e) => setFirstName(e.target.value)}
                  sx={{ mb: '1.5rem' }}
                />
                <Typography variant='subtitle2' component='h3' sx={{ fontWeight: 'bold' }}>
                  Last Name
                </Typography>
                <TextField
                  hiddenLabel
                  InputProps={{ 'aria-label': 'lastName' }}
                  fullWidth
                  size='small'
                  variant='outlined'
                  id='lastName'
                  type='url'
                  value={lastName || ''}
                  onChange={(e) => setLastName(e.target.value)}
                  sx={{ mb: '1.5rem' }}
                />

                {/* TITLE */}
                <Typography variant='subtitle2' component='h3' sx={{ fontWeight: 'bold' }}>
                  Title
                </Typography>
                <TextField
                  hiddenLabel
                  fullWidth
                  size='small'
                  variant='outlined'
                  id='title'
                  type='url'
                  value={title || ''}
                  onChange={(e) => setTitle(e.target.value)}
                  sx={{ mb: '1.5rem' }}
                />

                {/* ABOUT YOU */}
                <Typography variant='subtitle2' component='h3' sx={{ fontWeight: 'bold' }}>
                  Describe yourself in 2 sentences.
                </Typography>
                <TextField
                  inputProps={{ maxLength: 150 }}
                  hiddenLabel
                  multiline
                  rows={3}
                  fullWidth
                  size='small'
                  variant='outlined'
                  id='about'
                  type='url'
                  value={about || ''}
                  onChange={(e) => setAbout(e.target.value)}
                  sx={{ mb: '1.5rem' }}
                />
                {membershipLevel === 'member' && (
                  <div>
                    <Typography variant='subtitle2' component='h3' sx={{ fontWeight: 'bold' }}>
                      What can you offer advice about? (5 max)
                    </Typography>
                    <Select
                      fullWidth
                      size='small'
                      multiple
                      value={skills || ''}
                      onChange={handleSkillsChange}
                      sx={{ mb: '1.5rem' }}
                      input={<OutlinedInput id='select-multiple-chip' label='Chip' />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip key={value} label={value} />
                          ))}
                        </Box>
                      )}>
                      {skillsList.map((name) => (
                        <MenuItem key={name} value={name}>
                          {name}
                        </MenuItem>
                      ))}
                    </Select>
                  </div>
                )}
              </Paper>
            )}

            {(membershipLevel === 'member' || membershipLevel === 'member+mentor') && (
              <Paper
                elevation={20}
                sx={{
                  padding: '2rem',
                  mt: '2rem',
                  textAlign: 'left',
                  position: 'relative',
                }}>
                {!onboardingCompleted && membershipLevel !== 'mentor' && (
                  <Box
                    id='overlay'
                    sx={{
                      position: 'absolute',
                      margin: '-2rem',
                      width: '100%',
                      height: '100%',
                      backdropFilter: 'blur(8px)',
                      zIndex: '1000',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}>
                    <Button variant='contained'>Complete onboarding first</Button>
                  </Box>
                )}
                {/* COMPANY NAME */}
                <Typography variant='subtitle2' component='h3' sx={{ fontWeight: 'bold' }}>
                  Company Name
                </Typography>
                <TextField
                  hiddenLabel
                  fullWidth
                  size='small'
                  variant='outlined'
                  id='companyName'
                  type='url'
                  value={companyName || ''}
                  onChange={(e) => setCompanyName(e.target.value)}
                  sx={{ mb: '1.5rem' }}
                />

                {/* COMPANY LINK */}
                <Typography variant='subtitle2' component='h3' sx={{ fontWeight: 'bold' }}>
                  Company Link
                </Typography>
                <TextField
                  hiddenLabel
                  fullWidth
                  size='small'
                  variant='outlined'
                  id='companyName'
                  type='url'
                  value={companyLink || ''}
                  onChange={(e) => setCompanyLink(e.target.value)}
                  sx={{ mb: '1.5rem' }}
                />

                {/* ABOUT YOUR COMPANY */}
                <Typography variant='subtitle2' component='h3' sx={{ fontWeight: 'bold' }}>
                  Describe your company in one line
                </Typography>
                <TextField
                  hiddenLabel
                  multiline
                  rows={3}
                  fullWidth
                  size='small'
                  variant='outlined'
                  id='companyAbout'
                  type='url'
                  value={companyAbout || ''}
                  onChange={(e) => setCompanyAbout(e.target.value)}
                  sx={{ mb: '1.5rem' }}
                />
                {/* REVENUE */}
                <Typography variant='subtitle2' component='h3' sx={{ fontWeight: 'bold' }}>
                  What is your company ARR?
                </Typography>
                <Select
                  fullWidth
                  size='small'
                  value={revenue || ''}
                  onChange={(e) => setRevenue(e.target.value)}
                  sx={{ mb: '1.5rem' }}>
                  {revenueList.map((name) => (
                    <MenuItem key={name} value={name}>
                      {name}
                    </MenuItem>
                  ))}
                </Select>
              </Paper>
            )}

            {(membershipLevel === 'member' || membershipLevel === 'member+mentor') && (
              <Paper
                elevation={20}
                sx={{
                  padding: '2rem',
                  mt: '2rem',
                  textAlign: 'left',
                  position: 'relative',
                }}>
                {!onboardingCompleted && membershipLevel !== 'mentor' && (
                  <Box
                    id='overlay'
                    sx={{
                      position: 'absolute',
                      margin: '-2rem',
                      width: '100%',
                      height: '100%',
                      backdropFilter: 'blur(8px)',
                      zIndex: '1000',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}>
                    <Button variant='contained'>Complete onboarding first</Button>
                  </Box>
                )}
                {/* EMAIL */}
                <Typography variant='subtitle2' component='h3' sx={{ fontWeight: 'bold', mt: '1.5rem' }}>
                  Email
                </Typography>
                <TextField
                  hiddenLabel
                  disabled
                  fullWidth
                  size='small'
                  variant='outlined'
                  id='email'
                  type='url'
                  value={email || ''}
                  onChange={(e) => setEmail(e.target.value)}
                  sx={{ mb: '1.5rem' }}
                />

                {/* LINKEDIN */}
                <Typography variant='subtitle2' component='h3' sx={{ fontWeight: 'bold' }}>
                  LinkedIn
                </Typography>
                <TextField
                  hiddenLabel
                  fullWidth
                  size='small'
                  variant='outlined'
                  id='linkedin'
                  type='url'
                  value={linkedin || ''}
                  onChange={(e) => setLinkedIn(e.target.value)}
                  sx={{ mb: '1.5rem' }}
                />
              </Paper>
            )}

            {/* Address */}
            {(membershipLevel === 'member' || membershipLevel === 'member+mentor') && (
              <Paper
                elevation={20}
                sx={{
                  padding: '2rem',
                  mt: '2rem',
                  textAlign: 'left',
                  position: 'relative',
                }}>
                {!onboardingCompleted && membershipLevel !== 'mentor' && (
                  <Box
                    id='overlay'
                    sx={{
                      position: 'absolute',
                      margin: '-2rem',
                      width: '100%',
                      height: '100%',
                      backdropFilter: 'blur(8px)',
                      zIndex: '1000',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}>
                    <Button variant='contained'>Complete onboarding first</Button>
                  </Box>
                )}
                <Typography variant='subtitle2' component='h3' sx={{ fontWeight: 'bold', mt: '1.5rem' }}>
                  Address Line 1
                </Typography>
                <TextField
                  hiddenLabel
                  fullWidth
                  size='small'
                  variant='outlined'
                  id='addressLine1'
                  type='url'
                  value={addressLine1 || ''}
                  onChange={(e) => setAddressLine1(e.target.value)}
                  sx={{ mb: '1.5rem' }}
                />
                <Typography variant='subtitle2' component='h3' sx={{ fontWeight: 'bold' }}>
                  Address Line 2
                </Typography>
                <TextField
                  hiddenLabel
                  fullWidth
                  size='small'
                  variant='outlined'
                  id='addressLine2'
                  type='url'
                  value={addressLine2 || ''}
                  onChange={(e) => setAddressLine2(e.target.value)}
                  sx={{ mb: '1.5rem' }}
                />
                <Typography variant='subtitle2' component='h3' sx={{ fontWeight: 'bold' }}>
                  Postal Code
                </Typography>
                <TextField
                  hiddenLabel
                  fullWidth
                  size='small'
                  variant='outlined'
                  id='postalCode'
                  type='url'
                  value={postalCode || ''}
                  onChange={(e) => setPostalCode(e.target.value)}
                  sx={{ mb: '1.5rem' }}
                />
              </Paper>
            )}

            <Button type='submit' fullWidth variant='contained' sx={{ mt: 3, mb: 3 }}>
              Update Profile
            </Button>
          </Box>
        </Grid>
      )}

      <Button type='button' fullWidth variant='contained' color='secondary' onClick={() => supabase.auth.signOut()}>
        Sign Out
      </Button>
    </div>
  );
};

export default EditProfile;
